/**
 * UI Enhancements Module
 * Extracted from inline <script> in index.html for better code organization.
 * Handles: preloader, scroll progress, phone mask, dark mode, toast system,
 * counter animation, hero word animation, keyboard shortcuts.
 */

// === PRELOADER ===
export function initPreloader() {
  window.addEventListener("load", function () {
    const preloader = document.getElementById("preloader");
    if (preloader) {
      setTimeout(() => preloader.classList.add("hidden"), 400);
      setTimeout(() => preloader.remove(), 900);
    }
  });
}

// === SCROLL PROGRESS BAR ===
export function initScrollProgress() {
  const scrollProgress = document.getElementById("scrollProgress");
  window.addEventListener(
    "scroll",
    function () {
      const scrollTop =
        document.documentElement.scrollTop || document.body.scrollTop;
      const scrollHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const progress = (scrollTop / scrollHeight) * 100;
      if (scrollProgress) scrollProgress.style.width = progress + "%";
    },
    { passive: true },
  );
}

// === PHONE MASK (Brazilian format) ===
export function initPhoneMask() {
  function applyPhoneMask(input) {
    input.addEventListener("input", function (e) {
      let value = e.target.value.replace(/\D/g, "");
      if (value.length > 11) value = value.slice(0, 11);
      if (value.length > 6) {
        e.target.value =
          "(" +
          value.slice(0, 2) +
          ") " +
          value.slice(2, 7) +
          "-" +
          value.slice(7);
      } else if (value.length > 2) {
        e.target.value = "(" + value.slice(0, 2) + ") " + value.slice(2);
      } else if (value.length > 0) {
        e.target.value = "(" + value;
      }
    });
  }

  // Apply mask to all phone inputs
  document.querySelectorAll('input[type="tel"]').forEach(applyPhoneMask);

  // Watch for dynamically added phone inputs (budget modal)
  const phoneObserver = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) {
          const phoneInputs = node.querySelectorAll
            ? node.querySelectorAll('input[type="tel"]')
            : [];
          phoneInputs.forEach(applyPhoneMask);
          if (node.matches && node.matches('input[type="tel"]'))
            applyPhoneMask(node);
        }
      });
    });
  });
  phoneObserver.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// === DARK MODE TOGGLE ===
export function initDarkMode() {
  const darkModeToggle = document.getElementById("darkModeToggle");
  const darkModeIcon = document.getElementById("darkModeIcon");
  const savedDarkMode = localStorage.getItem("ytech-dark-mode");

  const SUN_ICON =
    '<circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>';
  const MOON_ICON = '<path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>';

  if (savedDarkMode === "true") {
    document.body.classList.add("dark-mode");
    if (darkModeIcon) darkModeIcon.innerHTML = SUN_ICON;
  }

  if (darkModeToggle) {
    darkModeToggle.addEventListener("click", function () {
      const isDark = document.body.classList.toggle("dark-mode");
      localStorage.setItem("ytech-dark-mode", isDark);
      if (darkModeIcon) {
        darkModeIcon.innerHTML = isDark ? SUN_ICON : MOON_ICON;
      }
    });
  }
}

// === TOAST NOTIFICATION SYSTEM ===
export function initToastSystem() {
  window.showToast = function (type, title, message, options) {
    options = options || {};
    const container = document.getElementById("toastContainer");
    if (!container) return;

    const toast = document.createElement("div");
    toast.className = "toast toast-" + type;

    const iconSvg =
      type === "success"
        ? '<svg class="toast-icon success" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>'
        : '<svg class="toast-icon error" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>';

    let fallbackHtml = "";
    if (type === "error" && options.showWhatsappFallback) {
      fallbackHtml =
        '<div class="toast-fallback"><a href="https://wa.me/5592984513964" target="_blank" rel="noopener">📱 Enviar via WhatsApp →</a></div>';
    }

    toast.innerHTML =
      iconSvg +
      '<div class="toast-content"><div class="toast-title">' +
      title +
      "</div>" +
      '<div class="toast-message">' +
      message +
      "</div>" +
      fallbackHtml +
      "</div>" +
      '<button class="toast-close" onclick="this.parentElement.remove()">×</button>';

    container.appendChild(toast);

    // Animate in
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        toast.classList.add("visible");
      });
    });

    // Auto-remove after duration
    var duration = options.duration || 5000;
    setTimeout(function () {
      toast.classList.remove("visible");
      setTimeout(function () {
        toast.remove();
      }, 400);
    }, duration);
  };
}

// === COUNTER ANIMATION (stat boxes) ===
export function initCounterAnimation() {
  const statObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const box = entry.target;
          const numberEl = box.querySelector(".stat-number");
          const target = parseInt(box.dataset.target) || 0;
          const suffix = box.dataset.suffix || "";

          if (box.classList.contains("animated")) return;
          box.classList.add("animated");

          let current = 0;
          const duration = 1500;
          const step = target / (duration / 16);

          function updateCounter() {
            current += step;
            if (current >= target) {
              current = target;
              numberEl.textContent = target + suffix;
              box.classList.add("count-done");
              return;
            }
            numberEl.textContent = Math.floor(current) + suffix;
            requestAnimationFrame(updateCounter);
          }
          requestAnimationFrame(updateCounter);
        }
      });
    },
    { threshold: 0.5 },
  );

  document.querySelectorAll(".stat-box[data-target]").forEach((el) => {
    statObserver.observe(el);
  });
}

// === HERO WORD ANIMATION ===
export function initHeroWordAnimation() {
  const heroTitle = document.getElementById("hero-title");
  if (!heroTitle) return;

  const html = heroTitle.innerHTML;
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  let globalWordIdx = 0;

  function wrapWords(node) {
    if (node.nodeType === 3) {
      // text node
      const words = node.textContent.split(/(\s+)/);
      const fragment = document.createDocumentFragment();
      words.forEach((word) => {
        if (word.trim() === "") {
          fragment.appendChild(document.createTextNode(word));
        } else {
          const span = document.createElement("span");
          span.className = "hero-word";
          span.textContent = word;
          span.style.animationDelay = globalWordIdx * 0.08 + 0.3 + "s";
          globalWordIdx++;
          fragment.appendChild(span);
        }
      });
      node.parentNode.replaceChild(fragment, node);
    } else if (node.nodeType === 1) {
      // element node — don't break spans with background-clip:text (gradient-text-animate)
      if (
        node.classList &&
        node.classList.contains("gradient-text-animate")
      ) {
        node.classList.add("hero-word");
        node.style.animationDelay = globalWordIdx * 0.08 + 0.3 + "s";
        globalWordIdx++;
        return;
      }
      Array.from(node.childNodes).forEach((child) => wrapWords(child));
    }
  }
  wrapWords(tempDiv);
  heroTitle.innerHTML = tempDiv.innerHTML;
}

// === KEYBOARD: Close modals with Escape ===
export function initKeyboardShortcuts() {
  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape") {
      const productModal = document.getElementById("productModal");
      const budgetModal = document.getElementById("budgetModal");
      if (productModal && productModal.classList.contains("active")) {
        if (typeof window.closeProductModal === "function") window.closeProductModal();
      }
      if (budgetModal && budgetModal.classList.contains("active")) {
        if (typeof window.closeBudgetModal === "function") window.closeBudgetModal();
      }
    }
  });
}

// === CONTACT FORM HANDLER ===
export function initContactFormHandler() {
  window.handleContactFormSubmit = async function (event) {
    event.preventDefault();

    const name = document.getElementById("contact-name").value;
    const email = document.getElementById("contact-email-input").value;
    const phone = document.getElementById("contact-phone-input").value;
    const subject = document.getElementById("contact-subject").value;
    const message = document.getElementById("contact-message").value;
    const website = document.getElementById("contact-website").value;
    const turnstileToken = window.YtechApi.getTurnstileToken("contact");

    const submitBtn = document.getElementById("contactSubmitBtn");
    const originalHTML = submitBtn.innerHTML;

    if (window.YtechApi.isTurnstileEnabled() && !turnstileToken) {
      const turnstileMessageEl = document.getElementById(
        "contactTurnstileMessage",
      );
      turnstileMessageEl.textContent =
        "Confirme a verificação anti-bot antes de enviar.";
      turnstileMessageEl.className = "form-message error";
      turnstileMessageEl.style.display = "block";
      return;
    }

    submitBtn.disabled = true;
    submitBtn.innerHTML = '<span class="btn-spinner"></span> Enviando...';

    try {
      await window.YtechApi.postJson("/api/contact", {
        name,
        email,
        phone,
        subject,
        message,
        website,
        turnstileToken,
      });

      const messageEl = document.getElementById("contactFormMessage");
      messageEl.textContent =
        "Mensagem enviada com sucesso! Nossa equipe entrará em contato em breve.";
      messageEl.className = "form-message success";
      messageEl.style.display = "block";
      document.getElementById("contactForm").reset();
      window.YtechApi.resetTurnstileWidget("contact");

      setTimeout(() => {
        messageEl.style.display = "none";
      }, 5000);
    } catch (err) {
      console.error("Erro ao enviar:", err);
      const messageEl = document.getElementById("contactFormMessage");
      messageEl.textContent =
        err.message ||
        "Erro ao enviar. Tente novamente ou entre em contato por WhatsApp.";
      messageEl.className = "form-message error";
      messageEl.style.display = "block";
      window.YtechApi.resetTurnstileWidget("contact");
    }

    submitBtn.disabled = false;
    submitBtn.innerHTML = originalHTML;
  };
}

// === INITIALIZE ALL UI ENHANCEMENTS ===
export function initAllUIEnhancements() {
  initPreloader();
  initScrollProgress();
  initPhoneMask();
  initDarkMode();
  initToastSystem();
  initCounterAnimation();
  initHeroWordAnimation();
  initKeyboardShortcuts();
  initContactFormHandler();
}
